/**
 * Arteriel PDP "More like this" strip (PR-C, Nova fused-embeddings).
 *
 * Progressive enhancement over snippets/more-like-this.liquid's SSR baseline
 * (rendered from product.metafields.arteriel.similar_artworks). Fetches the
 * live App Proxy more-like-this endpoint:
 *   GET {proxy}/search?product_id=<shopify product id>&limit=8[&market=<market id>]
 *   -> {"results":[{"product_id":…,"handle":…,"score":…}], …}
 * and, on ≥1 usable result, replaces the strip contents so PDP cards stay
 * pixel-identical to /search cards. Never throws on the PDP: any failure
 * (network, bad JSON, 404, empty result set) just leaves the SSR/metafield
 * state on screen untouched.
 *
 * Card shape mirrors assets/ai-search.js's cardData() (~line 60) and
 * buildCard() (~line 82) — do not modify ai-search.js; this file is a
 * standalone ES module (no deps), loaded only on the PDP.
 */

const RESULT_LIMIT = 8;

function withWidth720(src) {
  if (!src) return null;
  const sep = src.indexOf('?') === -1 ? '?' : '&';
  return `${src}${sep}width=720`;
}

// Mirrors assets/ai-search.js cardData(): map a /products/<handle>.js
// storefront product into the flat shape buildCard() below expects.
function cardData(p) {
  if (!p || !p.handle) return null;
  const img = p.featured_image || (p.images && p.images[0]) || null;
  return {
    id: p.id,
    handle: p.handle,
    url: `/products/${p.handle}`,
    title: p.title || '',
    artist: p.vendor || '',
    image: img ? { src: withWidth720(String(img)), alt: p.title || '' } : null,
  };
}

function el(tag, cls, text) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

// Mirrors assets/ai-search.js buildCard(): same classes/structure as
// snippets/ai-search-card.liquid's SSR markup, minus the favorites/trade
// quick-actions (not part of this strip's scope).
function buildCard(data) {
  const a = el('a', 'work');
  a.setAttribute('href', data.url);
  a.setAttribute('data-handle', data.handle);
  if (data.id) a.setAttribute('data-product-id', String(data.id));

  const frame = el('div', 'work__frame');
  if (data.image) {
    const img = document.createElement('img');
    img.setAttribute('src', data.image.src);
    img.setAttribute('alt', data.image.alt || data.title);
    img.setAttribute('loading', 'lazy');
    frame.appendChild(img);
  }
  a.appendChild(frame);

  const meta = el('div', 'work__meta');
  if (data.artist) meta.appendChild(el('div', 'work__artist', data.artist));
  meta.appendChild(el('div', 'work__title', data.title));
  a.appendChild(meta);

  return a;
}

// Fetch each result's product JSON and map to card data; failures/404s
// resolve to null and are dropped, never thrown.
async function hydrate(results) {
  const items = await Promise.all(
    results.map((r) =>
      fetch(`/products/${encodeURIComponent(r.handle)}.js`, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : null))
        .then(cardData)
        .catch(() => null),
    ),
  );
  return items.filter(Boolean);
}

function moreLikeThisUrl(proxy, productId, market) {
  const params = new URLSearchParams();
  params.set('product_id', productId);
  params.set('limit', String(RESULT_LIMIT));
  if (market) params.set('market', market);
  return `${proxy}/search?${params.toString()}`;
}

async function init(root) {
  const strip = root.querySelector('[data-strip]');
  if (!strip) return;

  const proxy = root.getAttribute('data-proxy') || '/apps/arteriel-ai';
  const productId = root.getAttribute('data-product-id') || '';
  const market = root.getAttribute('data-market') || '';
  if (!productId) return;

  try {
    const res = await fetch(moreLikeThisUrl(proxy, productId, market), { credentials: 'same-origin' });
    if (!res.ok) return; // leave the SSR/metafield state on screen
    const data = await res.json();
    const results = (data.results || [])
      .filter((r) => r && r.handle && String(r.product_id) !== String(productId))
      .slice(0, RESULT_LIMIT);
    if (!results.length) return;

    const items = (await hydrate(results)).slice(0, RESULT_LIMIT);
    if (!items.length) return; // every hydration failed — leave the SSR state alone

    while (strip.firstChild) strip.removeChild(strip.firstChild);
    items.forEach((data) => strip.appendChild(buildCard(data)));
    root.removeAttribute('hidden');
  } catch {
    // Never throw on the PDP — SSR/metafield state (or the hidden section) stands.
  }
}

function boot() {
  document.querySelectorAll('[data-mlt]').forEach((root) => init(root));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
